/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-12-20 12:30:39
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import { stringifyRGBASpecialFormat } from 'src/helpers/color-helper';
import { BUTTON_STYLES } from 'src/constants/quick-view-cart-settings';

/**
 * Build initial data for default layout settings
 *
 * @param  {Object} filterSettings filter settings from API
 *
 * @return {Object} initialValue
 */
export const buildInitialQuickViewCartSettingsValues = (filterSettings: Object, i18n: Object) => {
  const alStyleValue = _.get(filterSettings, 'actionlist.alStyle', 'bc-al-style4');
  const alStyleItem = _.find(BUTTON_STYLES, item => item.value === alStyleValue);

  const actionlist = {
    alStyle: { value: alStyleValue, label: i18n.translate(alStyleItem.label) },
    qvEnable: _.get(filterSettings, 'actionlist.qvEnable', false),
    qvBtnBackgroundColor: _.get(
      filterSettings,
      'actionlist.qvBtnBackgroundColor',
      'rgba(255||255||255||1)'
    ),
    qvBtnTextColor: _.get(filterSettings, 'actionlist.qvBtnTextColor', 'rgba(61||66||70||1)'),
    qvBtnBorderColor: _.get(
      filterSettings,
      'actionlist.qvBtnBorderColor',
      'rgba(255||255||255||1)'
    ),
    qvBtnHoverBackgroundColor: _.get(
      filterSettings,
      'actionlist.qvBtnHoverBackgroundColor',
      'rgba(61||66||70||1)'
    ),
    qvBtnHoverTextColor: _.get(
      filterSettings,
      'actionlist.qvBtnHoverTextColor',
      'rgba(255||255||255||1)'
    ),
    qvBtnHoverBorderColor: _.get(
      filterSettings,
      'actionlist.qvBtnHoverBorderColor',
      'rgba(61||66||70||1)'
    ),
    atcEnable: _.get(filterSettings, 'actionlist.atcEnable', false),
    atcBtnBackgroundColor: _.get(
      filterSettings,
      'actionlist.atcBtnBackgroundColor',
      'rgba(34||34||34||0.9)'
    ),
    atcBtnTextColor: _.get(filterSettings, 'actionlist.atcBtnTextColor', 'rgba(255||255||255||1)'),
    atcBtnBorderColor: _.get(
      filterSettings,
      'actionlist.atcBtnBorderColor',
      'rgba(34||34||34||0.9)'
    ),
    atcBtnHoverBackgroundColor: _.get(
      filterSettings,
      'actionlist.atcBtnHoverBackgroundColor',
      'rgba(61||66||70||1)'
    ),
    atcBtnHoverTextColor: _.get(
      filterSettings,
      'actionlist.atcBtnHoverTextColor',
      'rgba(255||255||255||1)'
    ),
    atcBtnHoverBorderColor: _.get(
      filterSettings,
      'actionlist.atcBtnHoverBorderColor',
      'rgba(61||66||70||1)'
    )
  };
  const general = {
    enableAjaxCart: _.get(filterSettings, 'general.enableAjaxCart', true),
    ajaxCartStyle: [_.get(filterSettings, 'general.ajaxCartStyle', 'slide')], // 'slide' or 'popup' or 'none'
    selectOptionInProductItem: _.get(filterSettings, 'general.selectOptionInProductItem', false)
      ? ['miniPopup']
      : ['popup'] // true: miniPopup, false: popup
  };
  return { actionlist, general };
};

/**
 * Build data for saving filter settings
 *
 * @param  {Object} data filter settings data
 * @param  {Object} filterSettings filter settings from API
 *
 * @return {Object} value value for sending API
 */
export const buildQuickViewCartSettingsValues = (data: Object, filterSettings: Object) => {
  let { actionlist: dataActionList, general: dataGeneral } = data;
  // Convert action list
  let actionlist = {};
  actionlist.alStyle = dataActionList.alStyle ? dataActionList.alStyle.value : 'bc-al-style4';
  actionlist.qvEnable = dataActionList.qvEnable ? true : false;
  actionlist.qvBtnBackgroundColor = convertColorToSpecialFormat(
    dataActionList.qvBtnBackgroundColor
  );
  actionlist.qvBtnTextColor = convertColorToSpecialFormat(dataActionList.qvBtnTextColor);
  actionlist.qvBtnBorderColor = convertColorToSpecialFormat(dataActionList.qvBtnBorderColor);
  actionlist.qvBtnHoverBackgroundColor = convertColorToSpecialFormat(
    dataActionList.qvBtnHoverBackgroundColor
  );
  actionlist.qvBtnHoverTextColor = convertColorToSpecialFormat(dataActionList.qvBtnHoverTextColor);
  actionlist.qvBtnHoverBorderColor = convertColorToSpecialFormat(
    dataActionList.qvBtnHoverBorderColor
  );
  actionlist.atcEnable = dataActionList.atcEnable ? true : false;
  actionlist.atcBtnBackgroundColor = convertColorToSpecialFormat(
    dataActionList.atcBtnBackgroundColor
  );
  actionlist.atcBtnTextColor = convertColorToSpecialFormat(dataActionList.atcBtnTextColor);
  actionlist.atcBtnBorderColor = convertColorToSpecialFormat(dataActionList.atcBtnBorderColor);
  actionlist.atcBtnHoverBackgroundColor = convertColorToSpecialFormat(
    dataActionList.atcBtnHoverBackgroundColor
  );
  actionlist.atcBtnHoverTextColor = convertColorToSpecialFormat(
    dataActionList.atcBtnHoverTextColor
  );
  actionlist.atcBtnHoverBorderColor = convertColorToSpecialFormat(
    dataActionList.atcBtnHoverBorderColor
  );

  let general = {};
  general.enableAjaxCart = dataGeneral.enableAjaxCart ? true : false;
  general.ajaxCartStyle = dataGeneral.ajaxCartStyle[0]; // 'slide' or 'popup' or 'none'
  general.selectOptionInProductItem =
    dataGeneral.selectOptionInProductItem[0] === 'miniPopup' ? true : false;

  return {
    ...filterSettings,
    actionlist: { ...filterSettings.actionlist, ...actionlist },
    general: { ...filterSettings.general, ...general }
  };
};

/**
 * Convert HSV color to RGBA string under format rbga(r||g||b||a)
 * @param  {object} hsvColor
 * @return {string}          rgba string color
 */
export const convertColorToSpecialFormat = (color: Object) => {
  // Return original color if it is not hsv
  if (!_.has(color, 'rgb')) {
    return color;
  }

  return stringifyRGBASpecialFormat(color.rgb);
};
